import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import type { MessageKey } from "@/i18n/catalogs";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import {
  PAD_COUNT,
  finishPlayback,
  initialState,
  playbackGap,
  pressPad,
  startGame,
  type EchoState,
  type Pad,
} from "./echoGame";

const BEST_KEY = "memoru-maintenance-echo-best";
/** Pad pitches in Hz: C4, E4, G4, C5. */
const TONES = [261.6, 329.6, 392, 523.3];

/**
 * Each pad has its own shape and number as well as its own colour, so none of
 * them depends on telling hues apart. Colours come from theme tokens that flip
 * in dark mode; the blue is the brand blue for the same reason.
 */
const PADS: ReadonlyArray<{ colour: string; glyph: string; name: MessageKey }> = [
  { colour: "[--pad:var(--primary)] dark:[--pad:oklch(0.78_0.11_240)]", glyph: "rounded-full", name: "maintenance.game.pad1" },
  { colour: "[--pad:var(--lilac-foreground)]", glyph: "rounded-md", name: "maintenance.game.pad2" },
  { colour: "[--pad:var(--success-foreground)]", glyph: "[clip-path:polygon(50%_8%,94%_90%,6%_90%)]", name: "maintenance.game.pad3" },
  { colour: "[--pad:var(--warning-foreground)]", glyph: "[clip-path:polygon(50%_0,100%_50%,50%_100%,0_50%)]", name: "maintenance.game.pad4" },
];

function readBest(): number {
  try {
    const stored = Number(window.localStorage.getItem(BEST_KEY));
    return Number.isInteger(stored) && stored > 0 ? stored : 0;
  } catch {
    return 0;
  }
}

/**
 * Echo, a pattern game to play while the app is closed. Tap or click the pads,
 * or press 1 to 4. It uses no network and no session.
 */
export function EchoGame({ className }: { className?: string }) {
  const { t } = useI18n();
  const [game, setGame] = useState<EchoState>(() => initialState(readBest()));
  const [lit, setLit] = useState<Pad | null>(null);
  const [sound, setSound] = useState(false);
  const gameRef = useRef(game);
  const soundRef = useRef(sound);
  const timers = useRef<number[]>([]);
  const flashTimer = useRef<number | undefined>(undefined);
  const audio = useRef<AudioContext | null>(null);

  soundRef.current = sound;

  useEffect(() => {
    if (game.best <= 0) return;
    try {
      window.localStorage.setItem(BEST_KEY, String(game.best));
    } catch {
      // The best score only lasts for this visit when storage is unavailable.
    }
  }, [game.best]);

  useEffect(() => () => {
    timers.current.forEach(window.clearTimeout);
    window.clearTimeout(flashTimer.current);
    void audio.current?.close().catch(() => undefined);
  }, []);

  const commit = (next: EchoState) => {
    gameRef.current = next;
    setGame(next);
  };

  const after = (ms: number, run: () => void) => {
    timers.current.push(window.setTimeout(run, ms));
  };

  /** Creates the audio context on first use. Safari keeps one made outside a tap suspended, so the sound switch calls this. */
  const getAudio = (): AudioContext | null => {
    try {
      const Context = window.AudioContext
        ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Context) return null;
      audio.current ??= new Context();
      if (audio.current.state === "suspended") void audio.current.resume().catch(() => undefined);
      return audio.current;
    } catch {
      return null;
    }
  };

  const beep = (pad: Pad) => {
    if (!soundRef.current) return;
    try {
      const context = getAudio();
      if (!context) return;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const now = context.currentTime;
      oscillator.type = "triangle";
      oscillator.frequency.value = TONES[pad];
      gain.gain.setValueAtTime(0.18, now);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start();
      oscillator.stop(now + 0.36);
    } catch {
      // Sound is a bonus: a blocked audio context must not break the game.
    }
  };

  const play = (sequence: Pad[], lead: number) => {
    const gap = playbackGap(sequence.length);
    sequence.forEach((pad, index) => {
      after(lead + index * gap, () => {
        setLit(pad);
        beep(pad);
      });
      after(lead + index * gap + gap * 0.6, () => setLit(null));
    });
    after(lead + sequence.length * gap, () => commit(finishPlayback(gameRef.current)));
  };

  const start = () => {
    timers.current.forEach(window.clearTimeout);
    timers.current = [];
    window.clearTimeout(flashTimer.current);
    setLit(null);
    const next = startGame(gameRef.current.best);
    commit(next);
    play(next.sequence, 500);
  };

  const press = (pad: Pad) => {
    const result = pressPad(gameRef.current, pad);
    if (result.outcome === "ignored") return;
    beep(pad);
    window.clearTimeout(flashTimer.current);
    setLit(pad);
    flashTimer.current = window.setTimeout(() => setLit(null), 180);
    commit(result.state);
    if (result.outcome === "round") after(900, () => play(result.state.sequence, 0));
  };

  // Listen on the window: the Play button unmounts when a game starts, which drops
  // focus to the body, so a handler on the card would never see the keys again.
  const onKeyRef = useRef<(event: globalThis.KeyboardEvent) => void>(() => undefined);
  onKeyRef.current = (event) => {
    if (event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest("input, textarea, select, [contenteditable]")) return;
    const number = Number(event.key);
    if (event.key.length === 1 && Number.isInteger(number) && number >= 1 && number <= PAD_COUNT) {
      event.preventDefault();
      press((number - 1) as Pad);
    }
  };
  useEffect(() => {
    const listener = (event: globalThis.KeyboardEvent) => onKeyRef.current(event);
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, []);

  const idle = game.phase === "idle" || game.phase === "over";
  let message = t("maintenance.game.idle");
  if (game.phase === "show") message = t("maintenance.game.watch");
  if (game.phase === "input") message = t("maintenance.game.yourTurn");
  if (game.phase === "over") message = t("maintenance.game.over", { count: game.score });

  return (
    <section
      aria-label={t("maintenance.game.label")}
      className={cn(
        // The card is as wide as the board the space allows (the board is square, so
        // the card is the board plus its padding).
        // Phones: the page puts the card in a size container, so 100cqw and 100cqh are
        // the space left on the screen. 6rem is the card's own height beyond its board
        // and width (padding 1.5rem, best-score row 2.75rem, two gaps, message line)
        // less the 1.5rem of padding that the width includes.
        "flex w-[min(100cqw,26rem,max(9rem,calc(100cqh_-_6rem)))] flex-col gap-3 rounded-xl border border-border bg-card p-3 min-[900px]:p-4",
        // Desktop: the card is a cell of an auto-sized column, where a percentage or
        // container width would collapse, so the height comes from the viewport.
        "min-[900px]:w-[min(26rem,max(9rem,calc(100dvh_-_var(--game-rest)_+_2rem)))]",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-baseline gap-2 text-sm text-muted-foreground">
          {t("maintenance.game.best")}
          <b className="text-2xl font-semibold leading-none text-foreground tabular-nums">{game.best}</b>
        </p>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-11 rounded-full bg-secondary text-muted-foreground aria-pressed:bg-accent aria-pressed:text-accent-foreground"
          aria-pressed={sound}
          aria-label={t("maintenance.game.sound")}
          onClick={() => {
            if (!sound) getAudio();
            setSound((on) => !on);
          }}
        >
          {sound ? <Volume2 aria-hidden="true" /> : <VolumeX aria-hidden="true" />}
        </Button>
      </div>

      <div className="relative grid grid-cols-2 gap-3">
        {PADS.map((pad, index) => (
          <button
            key={pad.name}
            type="button"
            aria-label={t(pad.name)}
            onClick={() => press(index as Pad)}
            className={cn(
              "relative flex aspect-square touch-manipulation select-none items-center justify-center rounded-3xl text-[color:var(--pad)] outline-none transition-colors",
              "bg-[color-mix(in_oklab,var(--pad)_14%,var(--card))] shadow-[inset_0_0_0_2px_color-mix(in_oklab,var(--pad)_40%,transparent)]",
              "focus-visible:ring-[3px] focus-visible:ring-ring/50",
              pad.colour,
              lit === index && "bg-[color:var(--pad)] text-card shadow-[0_0_0_4px_color-mix(in_oklab,var(--pad)_30%,transparent)]",
            )}
          >
            <span aria-hidden="true" className="absolute left-4 top-3 text-xs font-semibold pointer-coarse:hidden">{index + 1}</span>
            <span aria-hidden="true" className={cn("block aspect-square w-[30%] bg-current", pad.glyph)} />
          </button>
        ))}
        {/* The circle takes its height from its width (aspect-square on the child),
            never from a percentage height: Safari resolves a percentage height inside
            an aspect-ratio box against a different box than Chrome and drew an oval. */}
        <div className={cn("absolute left-1/2 top-1/2 w-[38%] min-w-[4.5rem] max-w-28 -translate-x-1/2 -translate-y-1/2 rounded-full bg-card p-2", !idle && "pointer-events-none")}>
          {idle ? (
            <Button type="button" className="aspect-square h-auto w-full rounded-full p-0" onClick={start}>
              {game.phase === "over" ? t("maintenance.game.again") : t("maintenance.game.play")}
            </Button>
          ) : (
            <div className="flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-full bg-secondary text-xs text-muted-foreground">
              {t("maintenance.game.round")}
              <b className="text-3xl font-semibold leading-none text-foreground tabular-nums">{game.sequence.length}</b>
            </div>
          )}
        </div>
      </div>

      <p aria-live="polite" className="min-h-6 text-center text-sm font-medium min-[900px]:text-base">{message}</p>
      <p className="text-center text-sm text-muted-foreground max-[899px]:hidden">{t("maintenance.game.how")}</p>
    </section>
  );
}
